import { ScoreImprovement, Score as SureScore } from "src/types";
import { Ssq, SsqScore } from "src/api/sure-api/ssq/useGetOneSsq";
import { RegulatoryScoreMapping } from "src/api/client-api/company/overview/useOrganizationOverviewOneSsq.tsx";
import { Score as ScoreTypeScore } from "src/types";
import { ScoreVariation as SureScoreVariation } from "src/types/applicationTypes/scores";
import { t } from "i18next";
import {
    MainScore,
    Dimension,
    DimensionValue,
    NumericalScoreValue,
    ScoreValue,
} from "src/api/client-api/olapScores/olapScoresSchema";
import { ScoreId } from "../context/OlapScores/useScoresIds";
import { Variations } from "../context/OlapScores/useVariations";
import { OlapScoresService } from "../context/OlapScores/OlapScoresService";
import { SsqVersionSsq } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";
import { orderVariations } from "src/utils/scores";

export type ScoreWithVariation = {
    rowId: number;
    label: string;
    group: string;
    scoreId: number;
    variationId: number | null;
    unit: string;
    onClick: () => void;
    precision: number;
    improvement: ScoreImprovement;
    datavizType: string;
    displayMode: string;
    type: string;
    disabled?: boolean;
    defaultDisplay?: boolean;
};

export class ScoreService {
    public static getScoresWithVariations(
        scores: SureScore[],
        variationsIds: number[],
        defaultVariationsIds: number[],
        variationOrder: number[],
        onVariationChange?: (scoreId: number, scoreVariationId: number | null) => void,
    ): ScoreWithVariation[] {
        let rowId = 0;

        return scores.flatMap((score: SureScore): ScoreWithVariation[] => {
            const mainScore = {
                rowId: rowId,
                scoreId: score.id,
                variationId: null,
                label: score.label ?? t("questionnaire.score_total", "Total score"),
                unit: score.unit?.abbreviation ?? "",
                onClick: () => onVariationChange?.(score.id, null),
                defaultDisplay: true,
                group: score.title,
                precision: score.precision ?? 3,
                improvement: score.improvement,
                datavizType: score.datavizType,
                displayMode: score.displayMode ?? "",
                type: score.type,
            };

            rowId += 1;

            const variations = orderVariations(score.variations, variationOrder).flatMap(
                (variation: SureScoreVariation): ScoreWithVariation[] => {
                    if (!variationsIds.includes(variation.id)) {
                        return [];
                    }

                    const variationRowId = rowId;
                    const defaultDisplay = defaultVariationsIds.includes(variation.id);

                    if (defaultDisplay === true) {
                        mainScore.defaultDisplay = false;
                    }

                    rowId += 1;

                    return [
                        {
                            ...mainScore,
                            rowId: variationRowId,
                            scoreId: score.id,
                            variationId: variation.id,
                            label: variation.label,
                            unit: variation.unit?.abbreviation ?? "",
                            onClick: () => onVariationChange?.(score.id, variation.id),
                            defaultDisplay: defaultDisplay,
                        },
                    ];
                },
            );

            return [mainScore, ...variations];
        });
    }

    public static getInitVariations(scoresWithVariations: ScoreWithVariation[]): Variations {
        return scoresWithVariations.reduce((acc: Variations, scoreWithVariation: ScoreWithVariation) => {
            if (scoreWithVariation.defaultDisplay) {
                acc[scoreWithVariation.scoreId] = scoreWithVariation.variationId;
            }

            return acc;
        }, {});
    }

    public static getScoreProperties = (scores: MainScore | undefined): DimensionValue[] => {
        if (scores === undefined || !scores.dimensions) {
            return [];
        }

        const propertiesDimensionValues = scores.dimensions.find(
            (dimension: Dimension) => dimension.label === "properties",
        )?.values;

        if (propertiesDimensionValues === undefined) return [];

        return propertiesDimensionValues.reduce<DimensionValue[]>((acc: DimensionValue[], value: DimensionValue) => {
            acc.push({
                ...value,
                score: {
                    value: OlapScoresService.normalizeNumericalScore(value.score.value),
                },
                dimensions: [],
            });

            return acc;
        }, []);
    };

    public static substractScore(startScore: NumericalScoreValue, endScore: NumericalScoreValue): NumericalScoreValue {
        if (endScore === null && startScore === null) {
            return null;
        }

        if (endScore === null && startScore !== null) {
            // Return * -1 ?
            // To negate it, and say :
            // if my start is 10, and current is unknown, then diff is -10;
            return startScore;
        }

        if (endScore !== null && startScore === null) {
            // Weird case, but eh.
            return endScore;
        }

        if (endScore !== null && startScore !== null) {
            if (typeof endScore === "number" && typeof startScore !== "number") {
                return endScore - startScore;
            }
        }

        return null;
    }

    public static addScore(base: NumericalScoreValue, toAdd: NumericalScoreValue): NumericalScoreValue {
        if (toAdd === null && base === null) {
            return null;
        }

        if (toAdd === null && base !== null) {
            return base;
        }

        if (toAdd !== null && base === null) {
            return toAdd;
        }

        if (toAdd !== null && base !== null) {
            if (typeof toAdd === "number" && typeof base === "number") {
                return toAdd + base;
            }
        }

        return null;
    }

    public static isScoreRegulatory(
        value: ScoreValue | RegulatoryScoreMapping | undefined,
        scoreDisplayMode: string,
    ): value is RegulatoryScoreMapping {
        if (value === undefined) return false;

        if (scoreDisplayMode === "regulation") {
            return true;
        }

        return false;
    }

    public static extractScoreValue(
        value: ScoreValue | RegulatoryScoreMapping | undefined,
        scoreDisplayMode: string,
    ): ScoreValue {
        if (value === undefined || value === null) {
            return null;
        }

        if (ScoreService.isScoreRegulatory(value, scoreDisplayMode)) {
            const score = Object.values(value).reduce(
                (acc, v) => {
                    const toAdd = v.required === true ? 1 : 0;
                    return (acc ?? 0) + toAdd;
                },
                null as number | null,
            );
            return score;
        }

        return value ?? null;
    }

    /**
     * Extrait les IDs de scores associés à un QSS à partir de ses formules
     * @param ssq - Le QSS à analyser
     * @param excludeDisplayModes - Les displayModes à exclure (par défaut: ["rules", "regulation"])
     */
    public static getScoreIdsFromSsq(
        ssq: SsqVersionSsq,
        excludeDisplayModes: string[] = ["rules", "regulation"],
    ): number[] {
        const ssqScores = ssq.scores.length ? ssq.scores : ssq.availableScores;

        return ssqScores.flatMap((score: SsqScore) => {
            if (excludeDisplayModes.length > 0 && excludeDisplayModes.includes(score.displayMode || "")) {
                return [];
            }

            return [score.id];
        });
    }

    /**
     * Filtre les scores de la plateforme pour ne garder que ceux associés à un SSQ spécifique
     * et qui ont des données dans les scores de l'entité
     * @param ssq - Le QSS à analyser
     * @param allPlatformScores - Tous les scores de la plateforme
     * @param excludeDisplayModes - Les displayModes à exclure (par défaut: ["rules", "regulation"])
     */
    public static getScoresForSsq(
        ssq: Ssq,
        allPlatformScores: SureScore[],
        excludeDisplayModes: string[] = ["rules", "regulation"],
    ): SureScore[] {
        const ssqScoreIds = ScoreService.getScoreIdsFromSsq(ssq, excludeDisplayModes);

        return allPlatformScores.filter((score) => {
            // Vérifier si ce score est associé à ce QSS spécifique
            const isScoreForThisSsq = ssqScoreIds.includes(score.id);

            return isScoreForThisSsq;
        });
    }

    /**
     * Find all SSQs containing a score of a specific type
     *
     * @param scoreDisplayMode - Le type de score à rechercher (ex: "regulation", "rules")
     * @param platformScores - Tous les scores de la plateforme
     * @param availableSsqs - Les SSQ disponibles pour la compagnie
     * @returns Tableau des SSQs qui contiennent le score du type demandé
     */
    public static findAllSsqsByScoreDisplayMode(
        scoreDisplayMode: string,
        platformScores: any[],
        availableSsqs: Ssq[],
    ): Ssq[] {
        const matchingSsqs: Ssq[] = [];

        availableSsqs.forEach((ssq) => {
            const ssqScores = ScoreService.getScoresForSsq(ssq, platformScores, []);
            const hasTargetDisplayMode = ssqScores.some((score) => score.displayMode === scoreDisplayMode);

            if (hasTargetDisplayMode) {
                matchingSsqs.push(ssq);
            }
        });

        return matchingSsqs;
    }

    /**
     * Extract SSQ IDs from company sector
     * @param companySector - Company sector object
     * @returns Array of SSQ IDs
     */
    public static extractSsqIdsFromCompany(companySector: any): number[] {
        if (!companySector?.ssqs || !Array.isArray(companySector.ssqs)) {
            return [];
        }

        return companySector.ssqs.map((s: any) => s.ssqId).filter((id: any) => typeof id === "number");
    }

    /**
     * Get SSQ for a company based on display mode - simplified interface
     *
     * @param selectedCompany - Company object
     * @param platformScores - Platform scores array
     * @param loadedSsqs - Loaded SSQ data
     * @param displayMode - Score display mode (e.g. "regulation")
     * @returns SSQ object or undefined
     */
    public static getSsqFromCompanyAndDisplayMode(
        selectedCompany: any,
        platformScores: any[],
        loadedSsqs: any[],
        displayMode: string,
    ): any | undefined {
        if (!selectedCompany || !loadedSsqs || loadedSsqs.length === 0) {
            return undefined;
        }

        // For simplified SSQ list, we can't check score content, so we return the last SSQ
        if (!loadedSsqs[0]?.parentSsqSsqHeadings) {
            return loadedSsqs[loadedSsqs.length - 1];
        }

        // For full SSQ objects, find by display mode
        const regulationSsqs = this.findAllSsqsByScoreDisplayMode(displayMode, platformScores, loadedSsqs as Ssq[]);
        return regulationSsqs.length > 0 ? regulationSsqs[regulationSsqs.length - 1] : undefined;
    }

    /**
     * Get score by display mode - simplified interface
     *
     * @param platformScores - Platform scores array
     * @param displayMode - Score display mode (e.g. "regulation")
     * @returns Score object or undefined
     */
    public static getScoreFromDisplayMode(platformScores: any[], displayMode: string): any | undefined {
        return platformScores.find((score) => score.displayMode === displayMode);
    }

    public static getLastAvailableSsqId(companySector?: any): number | undefined {
        const availableSsqs = companySector?.ssqs || [];
        const defaultSsqId = companySector?.primarySsqId || companySector?.ssqId;

        return availableSsqs.length > 0 ?
                availableSsqs[availableSsqs.length - 1].ssqId // Take the last available SSQ ID
            :   defaultSsqId;
    }

    public static getInitialVariations(scores: ScoreTypeScore[], defaultVariationsIds: number[]): Variations {
        return scores.reduce((acc: Variations, score: ScoreTypeScore) => {
            const variations = score.variations ?? [];
            const defaultVariation = variations.find((variation: SureScoreVariation) =>
                defaultVariationsIds.includes(variation.id),
            );

            if (defaultVariation !== undefined) {
                acc[score.id] = defaultVariation.id;
            }

            return acc;
        }, {});
    }

    public static getScoreIdsByLastSelectedVariation(
        lastSelectedVariation: ScoreWithVariation | null,
    ): ScoreId[] | undefined {
        if (lastSelectedVariation) {
            return [lastSelectedVariation.scoreId];
        }

        return undefined;
    }

    public static getVariationByLastSelectedVariation(
        lastSelectedVariation: ScoreWithVariation | null,
    ): Variations | undefined {
        if (lastSelectedVariation && lastSelectedVariation.variationId) {
            return {
                [lastSelectedVariation.scoreId]: lastSelectedVariation.variationId,
            };
        }

        return undefined;
    }

    public static getScoreWithVariationById(
        scoreId: number,
        scoresWithVariations: ScoreWithVariation[],
    ): ScoreWithVariation | undefined {
        return scoresWithVariations.find(
            (scoreWithVariation) => scoreWithVariation.scoreId === scoreId && scoreWithVariation.variationId === null,
        );
    }

    public static getVariationsByLastSelectedVariation(
        variations: Variations | undefined,
        lastSelectedVariation: ScoreWithVariation | null,
    ): Variations | undefined {
        if (!lastSelectedVariation) {
            return variations;
        }

        const filteredVariations = Object.fromEntries(
            Object.entries({
                ...variations,
                [lastSelectedVariation.scoreId]: lastSelectedVariation.variationId,
            }).filter(([_scoreId, variationId]) => variationId !== null),
        );

        if (Object.keys(filteredVariations).length > 0) {
            return filteredVariations;
        }

        return undefined;
    }
}
